import React from '../../vendor/react';
import { useEffect, useState } from '../../vendor/react';
import { Bell, ChevronDown, LogOut, Menu, Moon, Search, Sun, UserRound, Wifi } from '../../vendor/lucide-react';
import { Link, navigate, useLocation } from '../../vendor/react-router-dom';
import { useTheme } from '../../hooks/useTheme';
import { useWorkspace } from '../../features/workspace/useWorkspace';
import { getBusinessProfile } from '../../config/businessProfiles';
import { useOffline } from '../../features/offline/useOffline';
import { useSaas } from '../../features/saas/useSaas';
import { statusLabel } from '../../features/saas/saas-store';
import { CommandPalette } from './CommandPalette';
import { OperationalAlerts } from './OperationalAlerts';

const routeTitles: Record<string, { eyebrow: string; title: string }> = {
  '/app': { eyebrow: 'Vista general', title: 'Tu operación' },
  '/app/pos': { eyebrow: 'Operación', title: 'Punto de venta' },
  '/app/seleccionar-contexto': { eyebrow: 'Acceso', title: 'Negocios y sucursales' },
  '/app/cuenta': { eyebrow: 'Seguridad', title: 'Mi cuenta' },
  '/app/productos': { eyebrow: 'Catálogo maestro', title: 'Productos' },
  '/app/pedidos': { eyebrow: 'Seguimiento', title: 'Pedidos' },
  '/app/ventas': { eyebrow: 'Operación', title: 'Ventas' },
  '/app/inventario': { eyebrow: 'Existencias', title: 'Inventario' },
  '/app/clientes': { eyebrow: 'Relaciones', title: 'Clientes' },
  '/app/clientes-en-tienda': { eyebrow: 'Atención inteligente', title: 'Clientes en tienda' },
  '/app/proveedores': { eyebrow: 'Abastecimiento', title: 'Proveedores' },
  '/app/caja': { eyebrow: 'Control operativo', title: 'Caja y cortes' },
  '/app/reportes': { eyebrow: 'Análisis', title: 'Reportes' },
  '/app/transferencias': { eyebrow: 'Multisucursal', title: 'Transferencias' },
  '/app/membresias': { eyebrow: 'Ingreso recurrente', title: 'Membresías' },
  '/app/cobranza': { eyebrow: 'Cartera', title: 'Crédito y cobranza' },
  '/app/cierres': { eyebrow: 'Periodos', title: 'Cierres y análisis' },
  '/app/auditoria': { eyebrow: 'Control', title: 'Auditoría' },
  '/app/nexo-ia': { eyebrow: 'Copiloto', title: 'NEXO IA' },
  '/app/sincronizacion': { eyebrow: 'Disponibilidad', title: 'Sin conexión y sincronización' },
  '/app/plan': { eyebrow: 'Suscripción', title: 'Plan y licencia' },
  '/app/configuracion': { eyebrow: 'Personalización', title: 'Ajustes' },
};

export function AppHeader({ onOpenMenu }: { onOpenMenu: () => void }) {
  const location = useLocation();
  const workspace = useWorkspace();
  const offline = useOffline();
  const saas = useSaas();
  const profile = getBusinessProfile(workspace.business?.profileId);
  const baseCopy = routeTitles[location.pathname] ?? { eyebrow: 'NEXO', title: 'Módulo' };
  const copy = location.pathname.startsWith('/app/ventas/')
    ? { eyebrow: 'Comprobante', title: 'Ticket digital' }
    : location.pathname === '/app/productos'
      ? { eyebrow: 'Catálogo maestro', title: profile.productsLabel }
      : location.pathname === '/app/inventario'
        ? { eyebrow: 'Existencias', title: profile.inventoryLabel }
        : location.pathname === '/app/pos'
          ? { eyebrow: 'Operación', title: profile.posLabel }
          : baseCopy;
  const { theme, toggleTheme } = useTheme();
  const [profileOpen, setProfileOpen] = useState(false);
  const [commandOpen, setCommandOpen] = useState(false);
  const [alertsOpen, setAlertsOpen] = useState(false);
  const initials = `${workspace.session?.user.firstName?.[0] ?? ''}${workspace.session?.user.lastName?.[0] ?? ''}`.toUpperCase() || 'NE';
  const logout = async () => { await workspace.actions.signOut(); navigate('/ingresar', { replace: true }); };

  useEffect(() => {
    const listener = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setCommandOpen(true);
        setAlertsOpen(false);
        setProfileOpen(false);
      }
    };
    window.addEventListener('keydown', listener);
    return () => window.removeEventListener('keydown', listener);
  }, []);

  return <>
    <header className="app-header">
      <div className="app-header__title">
        <button className="icon-button mobile-menu" aria-label="Abrir menú" onClick={onOpenMenu}><Menu size={21} /></button>
        <div><span>{copy.eyebrow}</span><h1>{copy.title}</h1></div>
      </div>
      <div className="app-header__actions">
        <Link to="/app/seleccionar-contexto" className="header-context"><span>{workspace.business?.name}</span><small>{workspace.branch?.name}</small><ChevronDown size={15} /></Link>
        <Link to="/app/plan" className={`header-plan is-${saas.subscription?.status ?? 'trialing'}`} title="Plan y licencia"><span>{saas.plan?.shortName ?? saas.plan?.name ?? 'Plan'}</span><small>{saas.subscription ? statusLabel[saas.subscription.status] : 'Preparando'}</small></Link>
        <Link to="/app/sincronizacion" className={`header-sync is-${offline.connection}`} title="Estado de conexión y sincronización"><Wifi size={16}/><span>{offline.connection === 'local-only' ? 'Solo local' : offline.connection === 'offline' ? `${offline.pendingOperations} pendientes` : offline.pendingOperations ? `${offline.pendingOperations} por subir` : 'Sincronizado'}</span></Link>
        <button className="header-search" onClick={() => { setCommandOpen(true); setAlertsOpen(false); setProfileOpen(false); }}><Search size={18} /><span>Buscar o preguntar</span><kbd>Ctrl K</kbd></button>
        <button className="icon-button" aria-label={`Cambiar a modo ${theme === 'light' ? 'oscuro' : 'claro'}`} title="Cambiar modo de pantalla" onClick={toggleTheme}>{theme === 'light' ? <Moon size={19} /> : <Sun size={19} />}</button>
        <div className="notification-menu-wrap">
          <button className="icon-button has-dot" aria-label="Abrir alertas operativas" aria-expanded={alertsOpen} onClick={() => { setAlertsOpen((value) => !value); setProfileOpen(false); }}><Bell size={19} /></button>
          {alertsOpen && <OperationalAlerts onClose={() => setAlertsOpen(false)} />}
        </div>
        <div className="profile-menu-wrap">
          <button className="avatar-button" aria-label="Abrir perfil" aria-expanded={profileOpen} onClick={() => { setProfileOpen((value) => !value); setAlertsOpen(false); }}>{initials}</button>
          {profileOpen && <div className="profile-menu"><header><strong>{workspace.session?.user.firstName} {workspace.session?.user.lastName}</strong><span>{workspace.session?.user.email}</span><small>{workspace.roleLabel}</small></header><Link to="/app/cuenta" onClick={() => setProfileOpen(false)}><UserRound size={17} /> Mi cuenta</Link><button onClick={logout}><LogOut size={17} /> Cerrar sesión</button></div>}
        </div>
      </div>
    </header>
    <CommandPalette open={commandOpen} onClose={() => setCommandOpen(false)} />
  </>;
}
